<template>
	<div class="calculator calculator-repayment">
		<div class="container p-0">
			<div class="row flex-row calculator__row">
				<div class="col-lg-6 calculator__form ">
					<h2 class="text-start">{{ microcopy?.Heading }}</h2>
					<form>
						<div class="input-wrapper">
							<div class="row justify-content-between flex-nowrap has-tooltip">
								<span class="label form-label d-inline-block">{{ microcopy?.MortgageTypeLabel }}</span>
								<Tooltip :content="microcopy?.MortgageTypeTooltip" />
							</div>
							<div class="radio-container__wrapper">
								<label
									class="col radio-container radio-container--growth align-items-center justify-content-center d-flex text-center"
									:class="{ active: mortgageType == 'repayment' }"
									for="capital-payment"
								>
									{{ microcopy?.CapitalRepaymentLabel }}
									<input
										type="radio"
										id="capital-payment"
										value="repayment"
										v-model="mortgageType"
										name="mortgageType"
									/>
									<span class="radiobtn"></span>
								</label>
								<label
									class="col radio-container radio-container--growth align-items-center justify-content-center d-flex text-center"
									:class="{ active: mortgageType == 'interest' }"
									for="interest-only"
								>
									{{ microcopy?.InterestOnlyLabel }}
									<input
										type="radio"
										id="interest-only"
										value="interest"
										v-model="mortgageType"
										name="mortgageType"
									/>
									<span class="radiobtn"></span>
								</label>
							</div>
						</div>

						<TextInputField
							required
							:minimum-value="microcopy?.InvestmentMinimumInput"
							:maximum-value="microcopy?.InvestmentMaximumInput"
							input-type="percentage"
							:label-text="microcopy?.InterestRateLabel"
							:error-text="microcopy?.InterestRateValidationMessage"
							@field-input="(payload) => {
								interestRate = payload.value;
							}"
						>
							<template v-slot:tooltip>
								<Tooltip :content="microcopy?.InterestRateTooltip"></Tooltip>
							</template>
						</TextInputField>


						<TextInputField
							required
							:minimum-value="microcopy?.MortgageTermMinimumInput"
							:prefillInput="termPrefilled"
							:maximum-value="microcopy?.MortgageTermMaximumInput"
							input-type="years"
							:label-text="microcopy?.MortgageTermLabel"
							:error-text="microcopy?.MortgageTermValidationMessage"
							@field-input="(payload) => {
								term = +payload.value;
							}"
						>
							<template v-slot:tooltip>
								<Tooltip :content="microcopy?.MortgageTermTooltip"></Tooltip>
							</template>
						</TextInputField>

						<TextInputField
							required
							:minimum-value="microcopy?.MortgageAmountMinimumInput"
							:maximum-value="microcopy?.MortgageAmountMaximumInput"
							input-type="currency"
							:label-text="microcopy?.MortgageAmountLabel"
							:error-text="microcopy?.MortgageAmountValidationMessage"
							@field-input="(payload) => {
								amount = +payload.value;
							}"
						>
							<template v-slot:tooltip>
								<Tooltip :content="microcopy?.MortgageAmountTooltip"></Tooltip>
							</template>
						</TextInputField>
					</form>

					<button
						class="btn btn-primary btn-calculate"
						:disabled="buttonDisabled"
						@click="recalculate()"
					>
						{{ calculateButtonText }}
					</button>
				</div>
				<div class="results-container d-none d-lg-block col-lg-6 p-0">
					<div class="row">
						<div class="col">
							<div class="results">
								<h3 class="text-start">{{ microcopy?.ResultsPanelHeading }}</h3>
								<hr class="hr--small">
								<template v-if="this.mortgageType === 'repayment'">
									<div class="row">
										<div class="col text-start">
											<p>
												<span class="results-label"> {{ microcopy?.MonthlyRepaymentsLabel }} </span><br>
												<span class="results-amount">{{ formatCurrencyGBP(this.resultRepaymentMonthlyPayments) }}</span>
											</p>
										</div>
									</div>
									<hr class="hr--small">
									<div class="row">
										<div class="col text-start">
											<p>
												<span class="results-label"> {{ microcopy?.TotalCostOfMortgageLabel }} </span><br>
												<span class="results-amount">{{ formatCurrencyGBP(this.resultRepaymentTotalCostOfMortgage) }}</span>
											</p>
										</div>
									</div>
								</template>
								<template v-else>
									<div class="row">
										<div class="col text-start">
											<p>
												<span class="results-label"> {{ microcopy?.MonthlyRepaymentsLabel }} </span><br>
												<span class="results-amount">{{ formatCurrencyGBP(this.resultInterestOnlyMonthlyPayments) }}</span>
											</p>
										</div>
									</div>
									<hr class="hr--small">
									<div class="row">
										<div class="col text-start">
											<p>
												<span class="results-label"> {{ microcopy?.TotalInterestPayableLabel }}</span><br>
												<span class="results-amount">{{ formatCurrencyGBP(this.resultInterestOnlyTotalInterestPayable) }}</span>
											</p>
										</div>
									</div>
									<hr class="hr--small">
									<div class="row">
										<div class="col text-start">
											<p>
												<span class="results-label"> {{ microcopy?.TotalCostOfMortgageLabel }} </span><br>
												<span class="results-amount">{{ formatCurrencyGBP(this.resultInterestOnlyTotalCostOfMortgage) }}</span>
											</p>
										</div>
									</div>
								</template>
								<hr class="hr--small" />
								<div class="row">
									<div class="col text-start">
										<h3 class="results-heading">{{ microcopy?.CTATitle }}</h3>
										<p>{{ microcopy?.CTAParagraph }}</p>
										<a
											:href="microcopy?.CTALinkHref"
											class="arrow-btn arrow-btn--white"
										>{{ microcopy?.CTALinkText }}</a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="results-container-mobile d-block d-lg-none col-12 p-0 order-last results-container">
					<div class="container">
						<div class="row">
							<div class="col results mobile-results">
								<div class="d-lg-block">
									<template v-if="this.mortgageType === 'repayment'">
										<div class="row">
											<div class="col text-center text-lg-start">
												<p>
													<span class="results-label"> {{ microcopy?.MonthlyRepaymentsLabel }} </span><br>
													<span class="results-amount">{{ formatCurrencyGBP(this.resultRepaymentMonthlyPayments) }}</span>
												</p>
											</div>
										</div>
										<hr class="hr--small">
										<div class="row">
											<div class="col text-center text-lg-start">
												<p>
													<span class="results-label"> {{ microcopy?.TotalCostOfMortgageLabel }} </span><br>
													<span class="results-amount">{{ formatCurrencyGBP(this.resultRepaymentTotalCostOfMortgage) }}</span>
												</p>
											</div>
										</div>
									</template>
									<template v-else>
										<div class="row">
											<div class="col text-center text-lg-start">
												<p>
													<span class="results-label"> {{ microcopy?.MonthlyRepaymentsLabel }} </span><br>
													<span class="results-amount">{{ formatCurrencyGBP(this.resultInterestOnlyMonthlyPayments) }}</span>
												</p>
											</div>
										</div>
										<hr class="hr--small">
										<div class="row">
											<div class="col text-center text-lg-start">
												<p>
													<span class="results-label"> {{ microcopy?.TotalInterestPayableLabel }}</span><br>
													<span class="results-amount">{{ formatCurrencyGBP(this.resultInterestOnlyTotalInterestPayable) }}</span>
												</p>
											</div>
										</div>
										<hr class="hr--small">
										<div class="row">
											<div class="col text-center text-lg-start">
												<p>
													<span class="results-label"> {{ microcopy?.TotalCostOfMortgageLabel }} </span><br>
													<span class="results-amount">{{ formatCurrencyGBP(this.resultInterestOnlyTotalCostOfMortgage) }}</span>
												</p>
											</div>
										</div>
									</template>

									<hr class="hr--gold-2 hr--small mx-minus-20">

									<div class="row">
										<div class="col text-start">
											<h3 class="results-heading">{{ microcopy?.CTATitle }}</h3>
											<p>{{ microcopy?.CTAParagraph }}</p>
											<a
												:href="microcopy?.CTALinkHref"
												class="arrow-btn arrow-btn--white"
											>{{ microcopy?.CTALinkText }}</a>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					class="calculator__notification col"
					v-html="microcopy?.ComplianceMessage"
				></div>
			</div>
		</div>
	</div>
	<!-- </div> -->
	<!-- </div> -->
</template>

<script>
import helper from '../mixins/helpers.js';
import DropdownField from "../shared/components/DropdownField.vue";
import RadioInputComponent from "../shared/components/RadioInputComponent.vue";
import TextInputField from '../shared/components/TextInputField.vue';
import Tooltip from "../shared/components/Tooltip.vue";

import { pmt } from '../../lib/utils/math-helpers';

export default {
	components: {
		DropdownField,
		RadioInputComponent,
		TextInputField,
		Tooltip,
	},
	mixins: [
        helper
    ],
	data() {
		return {
			mortgageTypes: ['repayment', 'interest'],
			mortgageType: 'repayment',
			interestRate: '',
			term: '',
			amount: '',
			calculateButtonClicked: false,
			resultRepaymentMonthlyPayments: 0,
			resultRepaymentTotalCostOfMortgage: 0,
			resultInterestOnlyMonthlyPayments: 0,
			resultInterestOnlyTotalInterestPayable: 0,
			resultInterestOnlyTotalCostOfMortgage: 0,
			termDefaultValue: '',
		};
	},
	mounted() {
		this.termDefaultValue = this.microcopy?.MortgageTermDefaultInput || ''
	},
	computed: {
		termPrefilled() {
			return this.termDefaultValue.toString();
		},
		calculateButtonText() {
			return this.calculateButtonClicked == 0 ? this.microcopy?.CalculateButtonLabel : this.microcopy?.RecalculateButtonLabel;
		},
		buttonDisabled() {
			return !this.allInputsEntered || this.mortgageType == "" || this.interestRate === 0 || this.term === 0 || this.amount === 0;
		},
		allInputsEntered() {
			return this.mortgageType && this.interestRate && this.interestRate && this.term && this.amount;
		},
		microcopy() {
			return window.mortgageRepaymentMicrocopy;
		}
	},
	methods: {
		recalculate() {
			// Update displayed data

			// Repayment
			const R = (+this.interestRate / 100) / 12;
			const n = 12 * +this.term;
			const Pv = -1 * +this.amount;
			this.resultRepaymentMonthlyPayments = pmt(R, n, Pv);
			this.resultRepaymentTotalCostOfMortgage = this.resultRepaymentMonthlyPayments * this.term * 12;

			// Interest-only
			this.resultInterestOnlyMonthlyPayments = (+this.amount * (this.interestRate / 100)) / 12;
			this.resultInterestOnlyTotalInterestPayable = this.resultInterestOnlyMonthlyPayments * this.term * 12;
			this.resultInterestOnlyTotalCostOfMortgage = this.resultInterestOnlyTotalInterestPayable + this.amount;

			this.calculateButtonClicked = true;
            this.scrollToVisibleResults();
		},

		formatCurrencyGBP(value, digits = 0) {
			if (typeof value !== "number") {
				return value;
			}
			var formatter = new Intl.NumberFormat('en-GB', {
				style: 'currency',
				currency: 'GBP',
				minimumFractionDigits: digits,
				maximumFractionDigits: digits
			});
			return formatter.format(value);
		},
	},
}
</script>
